import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { line, BLOCKING_QUESTIONS, Color, EMPLOYMENT_QUESTION_CONFIDENTIALITY, EMPLOYMENT_QUESTION_DATE_SIGNED, EMPLOYMENT_QUESTION_NON_SOLICIT, EMPLOYMENT_QUESTION_RETURN_OR_DESTROY, NDA_QUESTIONS, Size, fill, isEmploymentReviewSet, suggestionRowsFor, t, type ColumnDef, type NdaScopeType, type NdaStatus, type PageSize, type SortState, type StringKey, type SuggestionRow, type SuggestionSource, viewerScopeKey } from '@bms2/shared';
import { Button, DatePicker, Dropdown, NumericInput, TextInput } from '../../components/controls';
import { SegmentedTab } from '../../components/controls/SegmentedTab';
import { Icon } from '../../components/Icon';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { ListViewer } from '../../components/composites/ListViewer';
import { PageModule } from '../../shell/PageModule';
import { PAGES_PATH, PICKER_CAP, apiFetch, post, useApi } from './api';
import { useCompanySearch } from '../settings/api';
import { NDA_PATH, NDA_REVIEW_ROUTE, NdaModal } from './NdaModal';
import styles from './RecordPage.module.css';
import wizard from './NdaReviewPage.module.css';
import { useAddNew } from './AddNewHost';
import { useNotice } from '../../components/controls/useNotice';

/**
 * The NDA review queue and the questionnaire (piece 9; decisions 0071, 0072).
 * The queue is the dashboard's "NDAs in review": every Draft or Demanded
 * agreement with what still blocks it, and the cover list beneath. The
 * questionnaire runs its eight questions in numeric order, one step at a time
 * (decision 1517); activation names its effect before it happens and is its
 * own audited act. Found while building 2026-09-09, built 2026-09-12.
 */
interface Names {
  counterparty: string | null;
  deal: string | null;
  person: string | null;
  file: string | null;
  permittedRecipients: { id: string; name: string }[];
}

interface QueueRow {
  id: string;
  scopeType: NdaScopeType;
  status: NdaStatus;
  effectiveDate: string;
  expiryDate: string | null;
  fileId: string | null;
  blockers: number[];
  names: Names;
}

interface CoverRow {
  counterpartyCompanyId: string;
  agreementIds: string[];
  coveredPersonIds: string[];
}

interface Review {
  questionNumber: number;
  confirmedAnswer: unknown;
  /** The machine's proposal, offered in the control and never pre-confirmed; no bot writes one yet. */
  detectedAnswer?: unknown;
}

interface Detail {
  id: string;
  scopeType: NdaScopeType;
  status: NdaStatus;
  counterpartyCompanyId: string | null;
  scopeDealId: string | null;
  scopePersonId: string | null;
  fileId: string | null;
  effectiveDate: string;
  expiryDate: string | null;
  requiresCover: boolean;
  enablesRelease: boolean;
  survivalMonths: number | null;
  permittedRecipientCompanyIds: string[];
  reviews: Review[];
  names: Names;
  blockers: number[];
}

const QUEUE_COLUMNS: readonly ColumnDef[] = [
  { key: 'counterparty', label: t('nda.queue.col.counterparty'), role: 'primary', type: 'text' },
  { key: 'scope', label: t('nda.queue.col.scope'), role: 'secondary', type: 'text' },
  { key: 'effective', label: t('nda.queue.col.effective'), role: 'secondary', type: 'text' },
  { key: 'blockers', label: t('nda.queue.col.blockers'), role: 'secondary', type: 'number' },
  { key: 'status', label: t('nda.queue.col.status'), role: 'status', type: 'status' },
];

function scopeText(row: { scopeType: NdaScopeType; names: Names }): string {
  if (row.scopeType === 'Deal') return line(t('nda.modal.deal'), '·', row.names.deal);
  if (row.scopeType === 'Person') return line(t('nda.modal.person'), '·', row.names.person);
  return t('nda.modal.scopeCompany');
}

export function NdaReviewPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const navigate = useNavigate();
  // 🔒 Decision 9861 (T3455): the list is the agreements in review, or the Active ones, which is where an employee's own agreement is read.
  const [which, setWhich] = useState<'review' | 'active'>('review');
  const queue = useApi<{ rows: QueueRow[] }>(which === 'active' ? `${NDA_PATH}?status=Active` : NDA_PATH, apiBaseUrl);
  const cover = useApi<{ rows: CoverRow[] }>(`${NDA_PATH}/cover`, apiBaseUrl);
  const [uploading, setUploading] = useState(false);
  const [sort, setSort] = useState<SortState | null>({ key: 'effective', direction: 'desc' });
  const [pageSize, setPageSize] = useState<PageSize>(50);
  return (
    <PageModule title={t('nda.queue.title')} newAction={<Button size={Size.Medium} color={Color.Navy} text={[t('nda.queue.upload')]} actions={[() => setUploading(true)]} />}>
      <p className={styles.hint}>{t('nda.queue.hint')}</p>
      <SegmentedTab
        name="ndaView"
        legend={t('nda.queue.view')}
        value={which}
        onChange={(next) => setWhich(next as 'review' | 'active')}
        segments={[{ value: 'review', label: t('nda.queue.view.review') }, { value: 'active', label: t('nda.queue.view.active') }]}
      />
      <AsyncStatePanel state={queue.state} emptyAction={which === 'active' ? t('nda.queue.emptyActive') : t('nda.queue.empty')} onRetry={queue.reload}>
        {(view) => (
          <div data-nda-queue={String(view.rows.length)}>
            <ListViewer
              viewStore={{ scopeKey: viewerScopeKey('nda-review'), apiBaseUrl }}
              recordType={t('nda.queue.title')}
              views={[{ id: 'all', name: t('nda.queue.title') }]}
              currentViewId="all"
              onViewChange={() => undefined}
              columns={QUEUE_COLUMNS}
              rows={view.rows.map((row) => ({ id: row.id, counterparty: row.names.counterparty ?? row.names.person ?? '—', scope: scopeText(row), effective: row.effectiveDate, blockers: row.blockers.length, status: row.status }))}
              idOf={(row) => row.id}
              read={(row, key) => (row as unknown as Record<string, unknown>)[key]}
              sort={sort}
              onSortChange={setSort}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              emptyAction={which === 'active' ? t('nda.queue.emptyActive') : t('nda.queue.empty')}
              onNavigate={(row) => navigate(`${NDA_REVIEW_ROUTE}/${row.id}`)}
            />
          </div>
        )}
      </AsyncStatePanel>

      <h3 className={styles.sectionTitle}>{t('nda.cover.title')}</h3>
      <p className={styles.hint}>{t('nda.cover.hint')}</p>
      <AsyncStatePanel state={cover.state} emptyAction={t('nda.cover.empty')} onRetry={cover.reload}>
        {(view) => (
          <ul className={styles.plainList} data-nda-cover={String(view.rows.length)}>
            {view.rows.length === 0 ? <li className={styles.hint}>{t('nda.cover.empty')}</li> : null}
            {view.rows.map((row) => (
              <li key={row.counterpartyCompanyId} className={styles.actions}>
                <button type="button" className={styles.linkButton} onClick={() => navigate(`/companies/${row.counterpartyCompanyId}`)}>{row.counterpartyCompanyId}</button>
                <span>{line(row.coveredPersonIds.length, t('nda.cover.covered'))}</span>
              </li>
            ))}
          </ul>
        )}
      </AsyncStatePanel>
      <NdaModal open={uploading} apiBaseUrl={apiBaseUrl} onCancel={() => setUploading(false)} onSaved={queue.reload} />
    </PageModule>
  );
}

function YesNo({ value, onChange }: { value: boolean | null; onChange: (v: boolean) => void }) {
  return (
    <span className={styles.actions}>
      <Button size={Size.Small} color={value === true ? Color.Navy : Color.Inactive} text={[t('nda.review.yes')]} actions={[() => onChange(true)]} pressed={value === true} />
      <Button size={Size.Small} color={value === false ? Color.Navy : Color.Inactive} text={[t('nda.review.no')]} actions={[() => onChange(false)]} pressed={value === false} />
    </span>
  );
}

/**
 * 🔒 An employee's agreement (decision 9680; T3368): written Active from an Employment Agreement's upload, its answers
 * the review. The card reads the date signed, the confidentiality clause, the non-solicit with its months, and the
 * return-or-destroy answer, beside the summary every agreement carries; Revoke and End the Obligation stand as on any
 * Active agreement.
 */
function EmploymentAgreementCard({ agreement: a, reason, onReason, onAct }: { agreement: Detail; reason: string; onReason: (value: string) => void; onAct: (verb: 'revoke' | 'end-obligation') => void }) {
  const answerOf = (n: number): unknown => a.reviews.find((r) => r.questionNumber === n)?.confirmedAnswer ?? null;
  const rec = (n: number): Record<string, unknown> => {
    const value = answerOf(n);
    return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
  };
  const yesNo = (value: unknown): string => (value === true ? t('nda.review.yes') : value === false ? t('nda.review.no') : t('nda.review.employment.unanswered'));
  const nonSolicit = rec(EMPLOYMENT_QUESTION_NON_SOLICIT);
  const months = typeof nonSolicit.months === 'number' ? nonSolicit.months : null;
  const nonSolicitText = nonSolicit.answer === true ? (months === null ? t('nda.review.yes') : line(t('nda.review.yes'), '·', fill(t('nda.review.employment.nonSolicitMonths'), { months: String(months) }))) : yesNo(nonSolicit.answer ?? null);
  return (
    <div className={wizard.wizard} data-nda-agreement={a.id} data-nda-status={a.status} data-nda-employment="true">
      <p className={wizard.note}>{t('nda.review.employment.hint')}</p>
      <dl className={wizard.summary} data-nda-summary>
        <div><dt>{t('nda.modal.person')}</dt><dd>{a.names.person ?? '—'}</dd></div>
        <div><dt>{t('nda.queue.col.status')}</dt><dd>{a.status}</dd></div>
        <div><dt>{t('nda.review.signedCopy')}</dt><dd>{a.names.file ?? t('nda.review.noSignedCopy')}</dd></div>
        <div><dt>{t('nda.review.employment.dateSigned')}</dt><dd data-nda-employment-answer={EMPLOYMENT_QUESTION_DATE_SIGNED}>{typeof rec(EMPLOYMENT_QUESTION_DATE_SIGNED).dateSigned === 'string' ? (rec(EMPLOYMENT_QUESTION_DATE_SIGNED).dateSigned as string) : a.effectiveDate.slice(0, 10)}</dd></div>
        <div><dt>{t('nda.review.employment.confidentiality')}</dt><dd data-nda-employment-answer={EMPLOYMENT_QUESTION_CONFIDENTIALITY}>{yesNo(answerOf(EMPLOYMENT_QUESTION_CONFIDENTIALITY))}</dd></div>
        <div><dt>{t('nda.review.employment.nonSolicit')}</dt><dd data-nda-employment-answer={EMPLOYMENT_QUESTION_NON_SOLICIT}>{nonSolicitText}</dd></div>
        <div><dt>{t('nda.review.employment.returnOrDestroy')}</dt><dd data-nda-employment-answer={EMPLOYMENT_QUESTION_RETURN_OR_DESTROY}>{yesNo(answerOf(EMPLOYMENT_QUESTION_RETURN_OR_DESTROY))}</dd></div>
      </dl>
      {a.status === 'Active' ? (
        <section className={wizard.body} data-nda-active-actions>
          <div className={wizard.control}>
            <TextInput size={Size.Large} label={t('nda.review.reason')} value={reason} onChange={onReason} name="nda-reason" />
            <Button size={Size.Small} color={Color.Inactive} text={[t('nda.review.revoke')]} actions={[() => onAct('revoke')]} />
            <Button size={Size.Small} color={Color.Inactive} text={[t('nda.review.endObligation')]} actions={[() => onAct('end-obligation')]} />
          </div>
        </section>
      ) : (
        <p className={wizard.note}>{t('nda.review.inactive')}</p>
      )}
    </div>
  );
}

/** The ninth step, after the eight questions (decision 1517). */
const ACTIVATE_STEP = NDA_QUESTIONS.length + 1;

/** Each question's short name on the rail, keyed by the question's own key. */
const STEP_NAMES: Readonly<Record<string, StringKey>> = {
  counterparty: 'nda.review.step.counterparty',
  requiresCover: 'nda.review.step.requiresCover',
  enablesRelease: 'nda.review.step.enablesRelease',
  scope: 'nda.review.step.scope',
  dates: 'nda.review.step.dates',
  survival: 'nda.review.step.survival',
  permittedRecipients: 'nda.review.step.permittedRecipients',
  returnOrDestroy: 'nda.review.step.returnOrDestroy',
};

function stepName(n: number): string {
  if (n === ACTIVATE_STEP) return t('nda.review.step.activate');
  const question = NDA_QUESTIONS.find((q) => q.number === n);
  const key = question ? STEP_NAMES[question.key] : undefined;
  return key ? t(key) : String(n);
}

/** The step marker: a filled check once confirmed, a ring on the current step, a hollow mark on an open one. */
function StepMarker({ n, done, current }: { n: number; done: boolean; current: boolean }) {
  const className = [wizard.marker, done ? wizard.done : '', current ? wizard.current : ''].filter(Boolean).join(' ');
  return (
    <span className={className} data-step-mark={done ? 'done' : current ? 'current' : 'open'} aria-hidden="true">
      {done ? <Icon meaning="confirmed" size="header" /> : n}
    </span>
  );
}

/**
 * The NDA review as a wizard (piece 9; decision 1517, on the owner's order of
 * 2026-10-02). A step rail names the eight questions and a ninth Activate
 * step; the body draws one question at a time with its detected answer
 * offered, and Confirm and next advances. A confirmed step reopens from the
 * rail. One summary card stands beside the rail, and its effects read the
 * answers the server derived from the confirmed rows. 💰 The stacked form read
 * as a sheet to fill, and the answered state was small grey text.
 */
export function NdaAgreementPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const addNew = useAddNew(apiBaseUrl);
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const detail = useApi<Detail>(id ? `${NDA_PATH}/${id}` : null, apiBaseUrl);
  const preview = useApi<{ blockers: number[]; lines: string[] }>(id ? `${NDA_PATH}/${id}/activation-preview` : null, apiBaseUrl);
  const [noticeBanner, setNotice] = useNotice();
  const [drafts, setDrafts] = useState<Record<number, unknown>>({});
  const [chosenStep, setChosenStep] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const companies = useCompanySearch(apiBaseUrl);
  const deals = useCallback(
    async (query: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
      const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/Deal?${PICKER_CAP}&q=${encodeURIComponent(query)}`, { signal });
      if (!response.ok) return [];
      const body = (await response.json()) as { rows: SuggestionSource[] };
      return suggestionRowsFor('Deal', body.rows);
    },
    [apiBaseUrl],
  );

  const reload = () => {
    detail.reload();
    preview.reload();
  };
  /** One answer confirmed through the review route; on success the step's draft is dropped and the wizard moves on. */
  const confirm = async (n: number, answer: unknown, next: number) => {
    setNotice(null);
    const outcome = await post(`${NDA_PATH}/${id}/review/${n}`, { answer }, apiBaseUrl);
    if (!outcome.ok) {
      setNotice({ tone: 'error', text: outcome.message });
      return;
    }
    setDrafts((was) => {
      const rest = { ...was };
      delete rest[n];
      return rest;
    });
    setChosenStep(next);
    reload();
  };
  const act = async (verb: 'activate' | 'revoke' | 'end-obligation') => {
    setNotice(null);
    const outcome = await post(`${NDA_PATH}/${id}/${verb}`, verb === 'activate' ? {} : { reason: reason.trim() || null }, apiBaseUrl);
    if (!outcome.ok) setNotice({ tone: 'error', text: outcome.message });
    else {
      setNotice({ tone: 'good', text: verb === 'activate' ? t('nda.review.activated') : t('settings.saved') });
      reload();
    }
  };
  const draft = (n: number) => drafts[n];
  const setDraft = (n: number, value: unknown) => setDrafts((was) => ({ ...was, [n]: value }));

  return (
    <PageModule title={t('nda.review.title')} newAction={<Button size={Size.Small} color={Color.Inactive} text={[t('nda.review.back')]} actions={[() => navigate(NDA_REVIEW_ROUTE)]} />}>
      {noticeBanner}
      <p className={styles.hint}>{t('nda.review.hint')}</p>
      <AsyncStatePanel state={detail.state} emptyAction={t('nda.queue.empty')} onRetry={reload}>
        {(a) => {
          // 🔒 Decision 9680: an employee's agreement was answered at its upload, so its card reads those answers and no questionnaire is drawn.
          if (isEmploymentReviewSet(a.reviews)) return <EmploymentAgreementCard agreement={a} reason={reason} onReason={setReason} onAct={(verb) => void act(verb)} />;
          const present = (value: unknown) => value !== null && value !== undefined;
          const answered = new Map(a.reviews.filter((r) => present(r.confirmedAnswer)).map((r) => [r.questionNumber, r.confirmedAnswer]));
          const detected = new Map(a.reviews.filter((r) => present(r.detectedAnswer)).map((r) => [r.questionNumber, r.detectedAnswer]));
          // The value a control shows: the person's draft, else the confirmed answer, else the detected one offered (never pre-confirmed).
          const held = (n: number): unknown => draft(n) ?? answered.get(n) ?? detected.get(n);
          const rec = (n: number): Record<string, unknown> => {
            const value = held(n);
            return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
          };
          const bool = (n: number): boolean | null => {
            const value = held(n);
            return value === true ? true : value === false ? false : null;
          };
          const recipients = Array.isArray(rec(7).companyIds) ? (rec(7).companyIds as string[]) : a.permittedRecipientCompanyIds;
          const recipientNames = new Map(a.names.permittedRecipients.map((r) => [r.id, r.name]));
          const named = (n: number) => rec(n).name as string | undefined;
          const editable = a.status === 'Draft' || a.status === 'Demanded';
          // A step opens once every blocking question below it is confirmed, as the review route requires; Activate always opens.
          const reachable = (n: number) => n === ACTIVATE_STEP || answered.has(n) || BLOCKING_QUESTIONS.every((b) => b >= n || answered.has(b));
          const firstOpen = editable ? (NDA_QUESTIONS.find((q) => !answered.has(q.number))?.number ?? ACTIVATE_STEP) : ACTIVATE_STEP;
          const step = chosenStep !== null && reachable(chosenStep) ? chosenStep : firstOpen;
          // Back goes to the nearest reachable step below, never past one it could open; step 1 is always reachable.
          const previous = (from: number): number => {
            for (let n = from - 1; n > 1; n -= 1) if (reachable(n)) return n;
            return 1;
          };
          const question = NDA_QUESTIONS.find((q) => q.number === step) ?? null;
          const openNamed = a.blockers.map((n) => `${n} · ${stepName(n)}`).join(', ');
          const blockedSentence = line(t('nda.review.blocked'), openNamed);

          /** An answer in words, for the detected line and the confirmed line. */
          const answerText = (n: number, value: unknown): string => {
            const asRecord = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
            if (n === 2 || n === 3 || n === 8) return value === true ? t('nda.review.yes') : value === false ? t('nda.review.no') : '';
            if (n === 1) return (asRecord.name as string | undefined) ?? a.names.counterparty ?? '';
            if (n === 4) return asRecord.scopeType === 'Deal' ? line(t('nda.modal.scopeDeal'), '·', (asRecord.name as string | undefined) ?? a.names.deal) : t('nda.modal.scopeCompany');
            if (n === 5) return line(asRecord.effectiveDate as string | undefined, '–', (asRecord.expiryDate as string | null | undefined) ?? '');
            if (n === 6) return typeof asRecord.months === 'number' ? line(asRecord.months, t('nda.review.monthsAfter')) : t('nda.review.perpetual');
            if (n === 7) {
              const ids = Array.isArray(asRecord.companyIds) ? (asRecord.companyIds as string[]) : [];
              return ids.length === 0 ? t('nda.review.noRecipients') : ids.map((c) => recipientNames.get(c) ?? c).join(', ');
            }
            return '';
          };

          /** Each question's control and the answer Confirm sends, or null while nothing is chosen. */
          const controlFor = (n: number): { control: ReactNode; answer: unknown | null } => {
            if (n === 1) {
              const companyId = (rec(1).companyId as string | undefined) ?? a.counterpartyCompanyId;
              return {
                control: <Dropdown size={Size.Medium} label={t('nda.modal.counterparty')} labelHidden entityLabel={t('nda.modal.counterpartyEntity')} search={companies} value={named(1) ?? a.names.counterparty ?? ''} onSelect={(row) => setDraft(1, { companyId: row.id, name: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord' }, (row) => setDraft(1, { companyId: row.id, name: row.line1 }))} />,
                answer: companyId ? { companyId } : null,
              };
            }
            if (n === 2 || n === 3 || n === 8) return { control: <YesNo value={bool(n)} onChange={(v) => setDraft(n, v)} />, answer: bool(n) };
            if (n === 4) {
              const scopeType = (rec(4).scopeType as NdaScopeType | undefined) ?? a.scopeType;
              const dealId = (rec(4).dealId as string | null | undefined) ?? a.scopeDealId;
              return {
                control: (
                  <>
                    <Button size={Size.Small} color={scopeType === 'Company' ? Color.Navy : Color.Inactive} text={[t('nda.modal.scopeCompany')]} actions={[() => setDraft(4, { scopeType: 'Company' })]} pressed={scopeType === 'Company'} />
                    <Button size={Size.Small} color={scopeType === 'Deal' ? Color.Navy : Color.Inactive} text={[t('nda.modal.scopeDeal')]} actions={[() => setDraft(4, { scopeType: 'Deal', dealId: a.scopeDealId })]} pressed={scopeType === 'Deal'} />
                    {scopeType === 'Deal' ? (
                      <Dropdown size={Size.Medium} label={t('nda.modal.deal')} labelHidden entityLabel={t('nda.modal.deal')} search={deals} value={named(4) ?? a.names.deal ?? ''} onSelect={(row) => setDraft(4, { scopeType: 'Deal', dealId: row.id, name: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'Deal' }, (row) => setDraft(4, { scopeType: 'Deal', dealId: row.id, name: row.line1 }))} />
                    ) : null}
                  </>
                ),
                answer: { scopeType, dealId },
              };
            }
            if (n === 5) {
              const effectiveDate = (rec(5).effectiveDate as string | undefined) ?? a.effectiveDate.slice(0, 10);
              const expiryDate = (rec(5).expiryDate as string | undefined) ?? a.expiryDate?.slice(0, 10) ?? '';
              return {
                control: (
                  <>
                    {/* A date picker holds an ISO day for a value, not for display, so these keep the stored date cut (T663). */}
                    <DatePicker size={Size.Small} label={t('nda.modal.effective')} value={effectiveDate} onChange={(v) => setDraft(5, { ...rec(5), effectiveDate: v })} name="review-effective" />
                    <DatePicker size={Size.Small} label={t('nda.modal.expiry')} value={expiryDate} onChange={(v) => setDraft(5, { ...rec(5), expiryDate: v })} name="review-expiry" />
                  </>
                ),
                answer: { effectiveDate, expiryDate: expiryDate || null },
              };
            }
            if (n === 6) {
              const months = typeof rec(6).months === 'number' ? (rec(6).months as number) : a.survivalMonths;
              return {
                control: <NumericInput size={Size.Small} label={t('nda.review.months')} kind="number" value={months} onChange={(v) => setDraft(6, { months: v })} name="review-survival" />,
                answer: { months },
              };
            }
            const names = (rec(7).names as Record<string, string> | undefined) ?? {};
            return {
              control: (
                <div className={wizard.recipients}>
                  <p className={wizard.note}>{t('nda.review.recipientsHint')}</p>
                  <ul className={wizard.recipients} data-nda-recipients={String(recipients.length)}>
                    {recipients.map((companyId) => (
                      <li key={companyId} className={wizard.recipient}>
                        <span>{recipientNames.get(companyId) ?? names[companyId] ?? companyId}</span>
                        <Button size={Size.Small} color={Color.Inactive} text={[t('nda.review.remove')]} actions={[() => setDraft(7, { companyIds: recipients.filter((c) => c !== companyId), names })]} />
                      </li>
                    ))}
                  </ul>
                  <Dropdown size={Size.Medium} label={t('nda.review.recipients')} labelHidden entityLabel={t('nda.modal.counterpartyEntity')} search={companies} value="" onSelect={(row) => setDraft(7, { companyIds: [...recipients.filter((c) => c !== row.id), row.id], names: { ...names, [row.id]: row.line1 } })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord' }, (row) => setDraft(7, { companyIds: [...recipients.filter((c) => c !== row.id), row.id], names: { ...names, [row.id]: row.line1 } }))} />
                </div>
              ),
              answer: { companyIds: recipients },
            };
          };

          const railWords = (n: number): string => {
            if (n === ACTIVATE_STEP) return a.blockers.length > 0 ? line(a.blockers.length, t('nda.review.open')) : a.status;
            const q = NDA_QUESTIONS.find((x) => x.number === n);
            return line(q?.required ? t('nda.review.required') : t('nda.review.optional'), '·', answered.has(n) ? t('nda.review.answered') : t('nda.review.open'));
          };

          return (
            <div className={wizard.wizard} data-nda-agreement={a.id} data-nda-status={a.status} data-nda-current-step={step}>
              <ol className={wizard.rail} aria-label={t('nda.review.steps')} data-nda-step-rail>
                {[...NDA_QUESTIONS.map((q) => q.number), ACTIVATE_STEP].map((n) => {
                  const done = n === ACTIVATE_STEP ? a.status === 'Active' : answered.has(n);
                  return (
                    <li key={n} data-nda-question={n === ACTIVATE_STEP ? undefined : n} data-nda-answered={done ? 'true' : 'false'}>
                      <button type="button" className={wizard.railButton} aria-current={n === step ? 'step' : undefined} disabled={!reachable(n)} onClick={() => setChosenStep(n)} data-nda-rail-step={n}>
                        <StepMarker n={n} done={done} current={n === step} />
                        <span className={wizard.railText}>
                          <span className={wizard.railName}>{stepName(n)}</span>
                          <span className={wizard.railWords}>{railWords(n)}</span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ol>

              <p className={wizard.folded} data-nda-folded-step>{line(t('nda.review.step'), step, t('nda.review.of'), ACTIVATE_STEP, '·', stepName(step))}</p>

              <dl className={wizard.summary} data-nda-summary>
                <div><dt>{t('nda.queue.col.counterparty')}</dt><dd>{a.names.counterparty ?? a.names.person ?? '—'}</dd></div>
                <div><dt>{t('nda.queue.col.scope')}</dt><dd>{scopeText(a)}</dd></div>
                <div><dt>{t('nda.queue.col.status')}</dt><dd>{a.status}</dd></div>
                <div><dt>{t('nda.review.signedCopy')}</dt><dd>{a.names.file ?? t('nda.review.noSignedCopy')}</dd></div>
                <div><dt>{t('nda.review.effects')}</dt><dd data-nda-effects>{[a.requiresCover ? t('nda.review.requiresCover') : null, a.enablesRelease ? t('nda.review.enablesRelease') : null].filter(Boolean).join(' · ') || t('nda.review.noEffects')}</dd></div>
              </dl>

              {question ? (() => {
                const { control, answer } = controlFor(question.number);
                const next = question.number + 1;
                const confirmedAnswer = answered.get(question.number);
                const detectedAnswer = detected.get(question.number);
                return (
                  <section className={wizard.body} data-nda-step={question.number}>
                    <p className={wizard.kicker}>{line(t('nda.review.question'), question.number, t('nda.review.of'), NDA_QUESTIONS.length, '·', question.required ? t('nda.review.required') : t('nda.review.optional'))}</p>
                    <h3 className={wizard.prompt}>{question.prompt}</h3>
                    {present(confirmedAnswer) ? (
                      <p className={wizard.confirmed} data-nda-confirmed-answer>
                        <Icon meaning="confirmed" size="inline" />
                        {line(t('nda.review.confirmedAs'), answerText(question.number, confirmedAnswer))}
                      </p>
                    ) : present(detectedAnswer) ? (
                      <p className={wizard.note}>{line(t('nda.review.detected'), answerText(question.number, detectedAnswer), t('nda.review.detectedConfirm'))}</p>
                    ) : (
                      <p className={wizard.note}>{t('nda.review.nothingDetected')}</p>
                    )}
                    {editable ? <div className={wizard.control}>{control}</div> : null}
                    <div className={wizard.nav}>
                      <Button size={Size.Small} color={Color.Inactive} text={[t('nda.review.previous')]} actions={[() => setChosenStep(previous(question.number))]} disabled={question.number === 1} disabledReason={t('nda.review.firstStep')} />
                      <span className={wizard.navForward}>
                        {editable && !question.required ? <Button size={Size.Small} color={Color.Inactive} text={[t('nda.review.skip')]} actions={[() => setChosenStep(next)]} /> : null}
                        {editable ? (
                          <Button size={Size.Small} color={Color.Navy} text={[t('nda.review.confirmNext')]} actions={[() => void confirm(question.number, answer, next)]} disabled={answer === null} disabledReason={t('nda.review.chooseFirst')} />
                        ) : null}
                      </span>
                    </div>
                  </section>
                );
              })() : (
                <section className={wizard.body} data-nda-step={ACTIVATE_STEP} data-nda-activation>
                  <p className={wizard.kicker}>{line(t('nda.review.step'), ACTIVATE_STEP, t('nda.review.of'), ACTIVATE_STEP)}</p>
                  <h3 className={wizard.prompt}>{t('nda.review.activation')}</h3>
                  {editable ? (
                    <>
                      <p className={wizard.note}>{t('nda.review.activationHint')}</p>
                      <ul className={wizard.effects} data-nda-preview>
                        {(preview.state.kind === 'ready' ? preview.state.data.lines : []).map((text) => (
                          <li key={text}>{text}</li>
                        ))}
                      </ul>
                      {a.blockers.length > 0 ? <p className={wizard.blockers} data-nda-blockers={a.blockers.join(',')}>{blockedSentence}</p> : null}
                      <div className={wizard.nav}>
                        <Button size={Size.Small} color={Color.Inactive} text={[t('nda.review.previous')]} actions={[() => setChosenStep(previous(ACTIVATE_STEP))]} />
                        <span className={wizard.navForward}>
                          <Button size={Size.Medium} color={Color.Navy} text={[t('nda.review.activate')]} actions={[() => void act('activate')]} disabled={a.blockers.length > 0} disabledReason={blockedSentence} />
                        </span>
                      </div>
                    </>
                  ) : a.status === 'Active' ? (
                    <div className={wizard.control} data-nda-active-actions>
                      <TextInput size={Size.Large} label={t('nda.review.reason')} value={reason} onChange={setReason} name="nda-reason" />
                      <Button size={Size.Small} color={Color.Inactive} text={[t('nda.review.revoke')]} actions={[() => void act('revoke')]} />
                      <Button size={Size.Small} color={Color.Inactive} text={[t('nda.review.endObligation')]} actions={[() => void act('end-obligation')]} />
                    </div>
                  ) : (
                    <p className={wizard.note}>{t('nda.review.inactive')}</p>
                  )}
                </section>
              )}
            </div>
          );
        }}
      </AsyncStatePanel>
      {addNew.host}
    </PageModule>
  );
}
